import type {
  ArchitectureData,
  Container,
  DrillLevel,
  ExternalSystem,
  Risk,
  RiskSeverity,
} from "@/lib/arch-parser";
import { clipSentence, firstSentence } from "./clip-sentence";
import { riskCategoryLabel } from "./plain-language";

// Types live in this file (not a separate types module) so knip doesn't flag
// them as unused exports between this task and the component task. The
// LevelSummary component imports these from "./level-summary".

export type NotableKind = "assumption" | "question" | "ops-note" | "decision";

export interface SummaryRisk {
  severity: RiskSeverity;
  description: string;
  /** Mitigation / alternative / accepted_because text, or null when the risk has no response logged. */
  response: string | null;
  /** The plain-language category label (e.g. "Cost", "Stuck with one vendor"). */
  categoryLabel: string;
  /** Owning element's node id, or null when no graph node exists (e.g. flow-level risks). */
  nodeId: string | null;
}

export interface SummaryNotable {
  kind: NotableKind;
  text: string;
  /** Optional deep-link node id for navigating from the summary to the source. */
  nodeId: string | null;
}

export interface InventoryGroup {
  /** Optional cluster name shown above the chip row (e.g. "Background workers"). */
  groupLabel?: string;
  chips: string[];
}

export interface LevelSummaryData {
  title: string;
  /** One sentence shown on the collapsed strip. Clipped to ~80 chars at a word boundary. */
  collapsedSentence: string;
  /** 2–3 sentence narrative shown when expanded. */
  expandedNarrative: string;
  inventory: InventoryGroup[];
  riskCounts: { critical: number; warning: number; info: number };
  risks: SummaryRisk[];
  notable: SummaryNotable[];
}

const COLLAPSED_MAX = 80;
const SEVERITY_ORDER: Record<RiskSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

function toSummaryRisk(risk: Risk, nodeId: string | null): SummaryRisk {
  return {
    severity: risk.severity,
    description: risk.description,
    response: risk.response?.text?.trim() || null,
    categoryLabel:
      risk.category === "other" && risk.category_label
        ? risk.category_label
        : riskCategoryLabel(risk.category),
    nodeId,
  };
}

function sortRisks(risks: SummaryRisk[]): SummaryRisk[] {
  return [...risks].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}

function countRiskSeverities(risks: SummaryRisk[]) {
  return risks.reduce(
    (acc, r) => {
      acc[r.severity] += 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 },
  );
}

function inventoryAtL1(arch: ArchitectureData): InventoryGroup[] {
  const groups: InventoryGroup[] = [];

  if (arch.actors.length > 0) {
    groups.push({
      groupLabel: "People",
      chips: arch.actors.map((a) => a.label),
    });
  }

  // Externals: group by cluster.label where set, ungrouped otherwise.
  const ungrouped: ExternalSystem[] = [];
  const byCluster = new Map<string, ExternalSystem[]>();
  for (const ext of arch.externalSystems) {
    if (ext.cluster) {
      const arr = byCluster.get(ext.cluster.label) ?? [];
      arr.push(ext);
      byCluster.set(ext.cluster.label, arr);
    } else {
      ungrouped.push(ext);
    }
  }
  for (const [label, items] of byCluster) {
    groups.push({ groupLabel: label, chips: items.map((e) => e.label) });
  }
  if (ungrouped.length > 0) {
    groups.push({
      groupLabel: "Outside services",
      chips: ungrouped.map((e) => e.label),
    });
  }

  return groups;
}

// Canvas node-ID convention: prefixed strings like `external:foo`,
// `container:foo`, `component:foo`. The system entity is the single bare
// `"system"` node at L1 (its slug isn't part of the canvas id). Decisions
// and questions are detail-only entities, conventionally `decision:foo`
// and `question:foo`. The LevelSummary's nodeId values must match this
// convention exactly — otherwise SELECT_NODE / SELECT_NODE_FOR_RISKS
// sets selectedNodeId to something the canvas doesn't render and the
// focus choreography dims every node (looks "all greyed out").
function notableFromArchitecture(arch: ArchitectureData): SummaryNotable[] {
  const items: SummaryNotable[] = [];
  const systemNodeId = arch.system ? "system" : null;

  // Unverified assumptions from system + externals (L1 scope).
  if (arch.system?.assumptions) {
    for (const a of arch.system.assumptions) {
      if (!a.verified)
        items.push({ kind: "assumption", text: a.description, nodeId: systemNodeId });
    }
  }
  for (const ext of arch.externalSystems) {
    for (const a of ext.assumptions ?? []) {
      if (!a.verified)
        items.push({
          kind: "assumption",
          text: a.description,
          nodeId: `external:${ext.id}`,
        });
    }
  }

  // Open / deferred questions.
  for (const q of arch.questions ?? []) {
    if (q.status === "open" || q.status === "deferred") {
      items.push({ kind: "question", text: q.title, nodeId: `question:${q.id}` });
    }
  }

  // Proposed / tradeoff decisions.
  for (const d of arch.decisions) {
    if (d.status === "proposed" || d.status === "accepted-with-known-tradeoff") {
      items.push({
        kind: "decision",
        text: d.title ?? d.label,
        nodeId: `decision:${d.id}`,
      });
    }
  }

  // Warn / critical ops notes.
  for (const n of arch.operationalNotes) {
    if (n.severity === "warning" || n.severity === "critical") {
      items.push({ kind: "ops-note", text: n.label, nodeId: null });
    }
  }

  return items;
}

function summarizeL1(arch: ArchitectureData): LevelSummaryData {
  const title = arch.system?.label ?? arch.meta.name ?? "System";
  const fullSentence = firstSentence(arch.system?.description ?? arch.meta.description ?? "");

  const allRisks: SummaryRisk[] = [];
  const systemNodeId = arch.system ? "system" : null;
  for (const r of arch.system?.risks ?? []) allRisks.push(toSummaryRisk(r, systemNodeId));
  for (const ext of arch.externalSystems) {
    for (const r of ext.risks ?? []) allRisks.push(toSummaryRisk(r, `external:${ext.id}`));
  }
  for (const f of arch.dataFlows) {
    for (const r of f.risks ?? []) allRisks.push(toSummaryRisk(r, null));
  }
  const sortedRisks = sortRisks(allRisks);

  return {
    title,
    collapsedSentence: clipSentence(fullSentence, COLLAPSED_MAX),
    expandedNarrative: fullSentence,
    inventory: inventoryAtL1(arch),
    riskCounts: countRiskSeverities(sortedRisks),
    risks: sortedRisks,
    notable: notableFromArchitecture(arch),
  };
}

function clusterKeyForContainer(c: Container): string {
  const kind = c.kind ?? "other";
  if (kind === "worker" || kind === "queue" || kind === "job") return "workers";
  if (kind === "frontend" || kind === "service" || kind === "api") return "bun-api-service";
  if (kind === "database" || kind === "cache" || kind === "storage") return "data-stores";
  return "other";
}

function inventoryAtL2(arch: ArchitectureData): InventoryGroup[] {
  const groups = new Map<string, Container[]>();
  for (const c of arch.containers) {
    const key = clusterKeyForContainer(c);
    const arr = groups.get(key) ?? [];
    arr.push(c);
    groups.set(key, arr);
  }

  const labelByKey: Record<string, string> = {
    "bun-api-service": "Bun API service",
    workers: "Background workers",
    "data-stores": "Data stores",
    other: "Other",
  };
  const ordered: InventoryGroup[] = [];
  for (const key of ["bun-api-service", "workers", "data-stores", "other"]) {
    const items = groups.get(key);
    if (!items || items.length === 0) continue;
    ordered.push({
      groupLabel: labelByKey[key],
      chips: items.map((c) => c.label),
    });
  }
  return ordered;
}

function l2NarrativeSentence(arch: ArchitectureData, groups: InventoryGroup[]): string {
  const containerCount = arch.containers.length;
  const systemName = arch.system?.label ?? arch.meta.name ?? "the system";
  if (containerCount === 0) return `Inside ${systemName}, no internal components are defined yet.`;
  const parts = groups
    .filter((g) => g.chips.length > 0)
    .map((g) => `${g.chips.length} ${g.groupLabel?.toLowerCase() ?? "component"}`);
  if (parts.length === 0) {
    return `Inside ${systemName}, ${containerCount} internal components.`;
  }
  const joined =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
  return `Inside ${systemName}: ${joined}.`;
}

function summarizeL2(arch: ArchitectureData): LevelSummaryData {
  const title = arch.system?.label ?? arch.meta.name ?? "System";
  const inventory = inventoryAtL2(arch);
  const sentence = l2NarrativeSentence(arch, inventory);

  const allRisks = arch.containers.flatMap((c) =>
    (c.risks ?? []).map((r) => toSummaryRisk(r, `container:${c.id}`)),
  );
  const sortedRisks = sortRisks(allRisks);

  const containerIds = new Set(arch.containers.map((c) => c.id));
  const notable: SummaryNotable[] = [];

  for (const c of arch.containers) {
    for (const a of c.assumptions ?? []) {
      if (!a.verified)
        notable.push({
          kind: "assumption",
          text: a.description,
          nodeId: `container:${c.id}`,
        });
    }
  }

  for (const d of arch.decisions) {
    if (d.status === "proposed" || d.status === "accepted-with-known-tradeoff") {
      if (
        !d.applies_to ||
        d.applies_to.length === 0 ||
        d.applies_to.some((id) => containerIds.has(id))
      ) {
        notable.push({
          kind: "decision",
          text: d.title ?? d.label,
          nodeId: `decision:${d.id}`,
        });
      }
    }
  }

  for (const q of arch.questions ?? []) {
    if (q.status === "open" || q.status === "deferred") {
      notable.push({ kind: "question", text: q.title, nodeId: `question:${q.id}` });
    }
  }

  for (const n of arch.operationalNotes) {
    if (n.severity === "warning" || n.severity === "critical") {
      if (
        !n.applies_to ||
        n.applies_to.length === 0 ||
        n.applies_to.some((id) => containerIds.has(id))
      ) {
        notable.push({ kind: "ops-note", text: n.label, nodeId: null });
      }
    }
  }

  return {
    title,
    collapsedSentence: clipSentence(sentence, COLLAPSED_MAX),
    expandedNarrative: sentence,
    inventory,
    riskCounts: countRiskSeverities(sortedRisks),
    risks: sortedRisks,
    notable,
  };
}

function summarizeL3(arch: ArchitectureData, containerId: string): LevelSummaryData {
  const container = arch.containers.find((c) => c.id === containerId);
  if (!container) return summarizeL2(arch);

  const title = container.label;
  const baseSentence = firstSentence(container.description ?? "");
  const techTail =
    (container.tech_stack ?? container.technology ?? []).length > 0
      ? `Built with ${(container.tech_stack ?? container.technology ?? []).join(", ")}.`
      : "";
  const expandedNarrative = [baseSentence, techTail].filter(Boolean).join(" ").trim();
  const collapsedSentence = clipSentence(
    baseSentence || techTail || `Part of ${arch.system?.label ?? "the system"}.`,
    COLLAPSED_MAX,
  );

  const componentChips = container.components.map((cmp) => cmp.label);
  const inventory: InventoryGroup[] =
    componentChips.length > 0 ? [{ groupLabel: "Pieces inside", chips: componentChips }] : [];

  const allRisks: SummaryRisk[] = [];
  for (const r of container.risks ?? []) {
    allRisks.push(toSummaryRisk(r, `container:${container.id}`));
  }
  for (const cmp of container.components) {
    for (const r of cmp.risks ?? []) allRisks.push(toSummaryRisk(r, `component:${cmp.id}`));
  }
  const sortedRisks = sortRisks(allRisks);

  const notable: SummaryNotable[] = [];
  for (const a of container.assumptions ?? []) {
    if (!a.verified)
      notable.push({
        kind: "assumption",
        text: a.description,
        nodeId: `container:${container.id}`,
      });
  }
  for (const cmp of container.components) {
    for (const a of cmp.assumptions ?? []) {
      if (!a.verified)
        notable.push({
          kind: "assumption",
          text: a.description,
          nodeId: `component:${cmp.id}`,
        });
    }
  }
  const scopeIds = new Set<string>([container.id, ...container.components.map((cmp) => cmp.id)]);
  for (const d of arch.decisions) {
    if (d.status === "proposed" || d.status === "accepted-with-known-tradeoff") {
      if (
        !d.applies_to ||
        d.applies_to.length === 0 ||
        d.applies_to.some((id) => scopeIds.has(id))
      ) {
        notable.push({
          kind: "decision",
          text: d.title ?? d.label,
          nodeId: `decision:${d.id}`,
        });
      }
    }
  }
  for (const q of arch.questions ?? []) {
    if (q.status === "open" || q.status === "deferred") {
      notable.push({ kind: "question", text: q.title, nodeId: `question:${q.id}` });
    }
  }
  for (const n of arch.operationalNotes) {
    if (n.severity === "warning" || n.severity === "critical") {
      if (
        !n.applies_to ||
        n.applies_to.length === 0 ||
        n.applies_to.some((id) => scopeIds.has(id))
      ) {
        notable.push({ kind: "ops-note", text: n.label, nodeId: null });
      }
    }
  }

  return {
    title,
    collapsedSentence,
    expandedNarrative,
    inventory,
    riskCounts: countRiskSeverities(sortedRisks),
    risks: sortedRisks,
    notable,
  };
}

export function summarize(
  arch: ArchitectureData,
  level: DrillLevel,
  selectedContainerId: string | null,
): LevelSummaryData {
  if (level === 1) return summarizeL1(arch);
  if (level === 2) return summarizeL2(arch);
  if (!selectedContainerId) return summarizeL2(arch);
  return summarizeL3(arch, selectedContainerId);
}
